import { createContext, useContext, useState, ReactNode } from "react";

export type Language = "de" | "en";

export const translations = {
  de: {
    calendlyUrl: "https://calendly.com/cem10x/ads",
    // Navbar
    nav: {
      services: "Leistungen",
      references: "Referenzen",
      career: "Werdegang",
      dubai: "Dubai",
      ai: "KI-SEO",
      shopify: "Shopify Ads",
      faq: "FAQ",
      contact: "Kontakt",
      cta: "Erstgespräch buchen",
    },
    // Hero
    hero: {
      badge: "M.Sc. in Online Marketing",
      headline1: "Planbares",
      headline2: "Wachstum durch Social Media Ads",
      subtext:
        "Ich übersetze deinen Content in messbare Ergebnisse – qualifizierte Leads, Bewerbungen, Buchungen, Verkäufe und Umsatz durch datengetriebenes Performance Marketing. Google Ads, Meta Ads, Facebook Ads, Instagram Ads, Shopify Ads, TikTok Ads, LinkedIn Ads & YouTube Ads.",
      profileTeaching: "Ehemaliger Dozent für die IHK und den TÜV für Social Media Marketing",
      cta1: "Kostenloses Erstgespräch",
      cta2: "Referenzen ansehen",
      yearsLabel: "Jahre Erfahrung",
      clientsLabel: "Zufriedene Kunden",
      platformsLabel: "Ad-Plattformen",
    },
    // About
    about: {
      sectionLabel: "Über mich",
      headline: "Strategie trifft\nPerformance",
      text1:
        "Ich berate Unternehmen, Behörden und Bildungseinrichtungen dabei, bezahlte Marketingkanäle gezielt und messbar einzusetzen – von Google Ads über Meta Ads, Facebook Ads und Instagram Ads bis zu Shopify Ads, TikTok Ads, LinkedIn Ads und YouTube Ads.",
      text2:
        "Als Lehrbeauftragter von Hochschulen, Dozent für die IHK und den TÜV verbinde ich akademische Tiefe mit konkreter Praxiserfahrung. Ich analysiere, wo Ihr größter Hebel liegt – und setze die richtigen Maßnahmen in der richtigen Reihenfolge um.",
      approachLabel: "Mein Ansatz",
      approach: [
        "Datengetriebene Entscheidungen statt Bauchgefühl",
        "Kanalübergreifende Strategie & Umsetzung",
        "Transparente Kommunikation & Reporting",
        "Budgetsteuerung, Testing-Roadmap & Skalierung",
        "Scaling & Effizienz",
      ],
    },
    // References
    references: {
      sectionLabel: "Referenzen",
      headline: "Vertrauen von\nführenden Organisationen",
      subtext:
        "Von DAX-Konzernen und Bundesministerien bis hin zu internationalen Botschaften und Sparkassen – ein Auszug meiner Referenzen als Performance Marketing Berater.",
      categories: [
        { id: "corporate", label: "Unternehmen", note: null },
        { id: "public", label: "Öffentlicher Sektor", note: null },
        { id: "education", label: "Dozententätigkeiten / Lehrbeauftragter", note: null },
      ],
      notePrefix: "Weitere Unternehmen im Gesundheitswesen:",
      educationUsps: [
        { institution: "IHK Online Marketing Zertifikat", text: "Dozent für Kurse zur Vorbereitung auf das IHK-Zertifikat im Online Marketing." },
        { institution: "TÜV · Regierungsorganisationen", text: "Trainer für Schulungen von Regierungsorganisationen im Auftrag des TÜV." },
        { institution: "Beuth Hochschule Berlin", text: "Lehrbeauftragter für Betriebswirtschaftslehre an der Beuth Hochschule für Technik Berlin." },
      ],
    },
    // Stats
    stats: {
      sectionLabel: "Kennzahlen",
      headline1: "Messbare Ergebnisse,",
      headline2: "die zählen",
      subtext:
        "Zahlen, die für sich sprechen – aus über zwei Jahrzehnten Performance Marketing, Google Ads und Meta Ads Beratung.",
      items: [
        { value: "20+", label: "Jahre Erfahrung", desc: "im Performance Marketing" },
        { value: "80+", label: "Kunden & Projekte", desc: "national & international" },
        { value: "10+", label: "Ad-Plattformen", desc: "vollständige Expertise" },
        { value: "30+", label: "Botschaften & Behörden", desc: "öffentlicher Sektor" },
      ],
    },
    // Services — exakt aus den Screenshots
    services: {
      sectionLabel: "Leistungen",
      headline1: "Was ich für Sie",
      headline2: "leisten kann",
      subtext:
        "Von Google Ads und Meta Ads über Facebook Ads, Instagram Ads und Shopify Ads bis zu TikTok Ads, LinkedIn Ads und YouTube Ads – ich verbinde die relevanten Kanäle zu messbarem Performance Marketing.",
      featuredBadge: "Kernleistung",
      items: [
        {
          title: "Performance Marketing",
          subtitle: "Datengetrieben. Messbar. Skalierbar.",
          description:
            "Ich entwickle und steuere Performance-Kampagnen über Google Ads, Meta Ads, Facebook Ads, Instagram Ads, Shopify Ads, TikTok Ads, LinkedIn Ads und YouTube Ads – von der Strategie über das Tracking bis zur kontinuierlichen Optimierung. Jede Maßnahme ist auf messbare KPIs ausgerichtet.",
          tags: ["Google Ads", "Meta Ads", "Facebook Ads", "Instagram Ads", "Shopify Ads", "TikTok Ads", "LinkedIn Ads", "YouTube Ads"],
        },
        {
          title: "Google Ads & Analytics",
          subtitle: "Search, Shopping, Display & mehr",
          description:
            "Vollständige Google-Ökosystem-Expertise: Search, Shopping, Display, Performance Max, Discovery und YouTube Ads – kombiniert mit sauberem Conversion-Tracking und Attribution.",
          tags: ["Search", "Shopping", "PMax", "GA4"],
        },
        {
          title: "Meta & Social Ads",
          subtitle: "Facebook Ads, Instagram Ads & mehr",
          description:
            "Bezahlte Meta Ads auf Facebook und Instagram – ergänzt um Shopify Ads, TikTok Ads, LinkedIn Ads und Click-to-WhatsApp-Kampagnen. Ich verbinde Creatives, Zielgruppen, Angebot und Tracking zu einer nachvollziehbaren Social-Ads-Strategie.",
          tags: ["Meta Ads", "Facebook Ads", "Instagram Ads", "Shopify Ads", "TikTok Ads", "LinkedIn Ads"],
        },
        {
          title: "SEO & LLM (AI) Sichtbarkeit",
          subtitle: "Organisches Wachstum",
          description:
            "Nachhaltige Suchmaschinenoptimierung für Google und Bing – ergänzt um eine klare Informationsarchitektur für KI-Sucherlebnisse. Ich strukturiere relevante Inhalte rund um Google Ads, Meta Ads, Facebook Ads, Instagram Ads und E-Commerce so, dass sie für Menschen, Suchmaschinen und KI-Systeme wie ChatGPT, Claude, Perplexity oder Google AI verständlich und auffindbar sind.",
          tags: ["On-Page SEO", "Technical SEO", "LLM / GEO", "ChatGPT", "Claude", "Perplexity"],
        },
        {
          title: "Business Consulting",
          subtitle: "Strategische Beratung",
          description:
            "Ich finde Lösungen, die keiner dominanten Logik entsprechen. Strategische Beratung für Unternehmen, die neue Wachstumspfade erschließen wollen.",
          tags: ["Strategie", "Wachstum", "Innovation"],
        },
        {
          title: "Schulungen & Workshops",
          subtitle: "Wissen transferieren",
          description:
            "Als Dozent für die IHK, den TÜV und als Lehrbeauftragter Hochschulen vermittelte ich Online-Marketing-Wissen praxisnah und anwendungsorientiert – für Teams und Einzelpersonen.",
          tags: ["IHK", "TÜV", "Hochschule", "Corporate"],
        },
      ],
    },
    // Education — exakt aus dem Screenshot
    education: {
      sectionLabel: "Akademischer Werdegang",
      headline1: "Fundiertes Wissen,",
      headline2: "globale Perspektive",
      subtext:
        "Mein akademischer Hintergrund verbindet technisches Online-Marketing-Know-how und Performance Marketing Expertise mit internationaler Business-Perspektive – von Berlin über New York bis Mailand.",
      items: [
        {
          degree: "M.Sc. Online Marketing",
          institution: "Steinbeis School of Management & Innovation",
          location: "Berlin & München",
          note: "Spezialisierung auf Performance Marketing & Digitale Strategien",
        },
        {
          degree: "NYU Stern School of Business",
          institution: "New York University",
          location: "New York, USA",
          note: "Financial Times: One of the Best Business Schools worldwide",
        },
        {
          degree: "Bocconi School of Management",
          institution: "Università Bocconi",
          location: "Mailand, Italien",
          note: "One of the Best International Business Schools",
        },
        {
          degree: "B.A. in Mediamanagement",
          institution: "M.DH Hochschule für Design & Informatik",
          location: "Berlin",
          note: "Grundlage für digitales Marketing und Medienstrategie",
        },
      ],
    },
    // CTA
    cta: {
      badge: "Jetzt Gespräch vereinbaren",
      headline1: "Wo liegt dein",
      headline2: "größter Hebel?",
      subtext:
        "In einem kurzen, kostenfreien Erstgespräch analysieren wir gemeinsam, welche Google Ads, Meta Ads oder SEO-Maßnahmen in welcher Reihenfolge am schnellsten Wirkung erzeugen – konkret, messbar und ohne Umwege.",
      points: [
        "Erstgespräch – kostenlos & unverbindlich",
        "Analyse Ihrer aktuellen Marketing-Situation",
        "Konkrete Empfehlungen mit Prioritäten",
      ],
      ctaButton: "Kostenfreies Erstgespräch vereinbaren",
      contactTitle: "Kontakt",
      emailLabel: "E-Mail",
      locationLabel: "Standort",
      location1: "Dubai, UAE",
      location2: "& Berlin, Deutschland",
      companyLabel: "Unternehmen",
      companyName: "Cem10X Marketing FZCO",
      companyDesc: "Dubai Silicon Oasis, IFZA",
      footerNote:
        "Ich berate Unternehmen national und international. Sprechen Sie mich gerne auf Deutsch oder Englisch an.",
    },
    // Footer
    footer: {
      tagline: "Performance Marketing · Berlin & Dubai",
      copyright: "Alle Rechte vorbehalten.",
      platformsTitle: "Plattformen",
      impressumTitle: "Impressum",
      company: "Cem10X Marketing FZCO",
      address: "Dubai Silicon Oasis, DDP Building A2, IFZA",
      country: "United Arab Emirates",
      responsible: "Inhaltlich verantwortlich: Cem Fernández-Yigit, M.Sc.",
      phoneUAE: "Mobile UAE",
      phoneGER: "Mobile GER",
    },
  },
  en: {
    calendlyUrl: "https://calendly.com/cem10x/meeting-for-ads",
    // Navbar
    nav: {
      services: "Services",
      references: "References",
      career: "Career",
      dubai: "Dubai",
      ai: "AI SEO",
      shopify: "Shopify Ads",
      faq: "FAQ",
      contact: "Contact",
      cta: "Book a Call",
    },
    // Hero
    hero: {
      badge: "M.Sc. in Online Marketing",
      headline1: "Predictable",
      headline2: "Growth through social media ads",
      subtext:
        "I turn your content into measurable results – qualified leads, applications, bookings, sales and revenue through data-driven performance marketing. Google Ads, Meta Ads, Facebook Ads, Instagram Ads, Shopify Ads, TikTok Ads, LinkedIn Ads & YouTube Ads.",
      profileTeaching: "Former instructor for the IHK and TÜV in social media marketing",
      cta1: "Free Consultation",
      cta2: "View References",
      yearsLabel: "Years Experience",
      clientsLabel: "Happy Clients",
      platformsLabel: "Ad Platforms",
    },
    // About
    about: {
      sectionLabel: "About me",
      headline: "Strategy meets\nPerformance",
      text1:
        "I advise companies, public authorities and educational institutions on how to use paid marketing channels in a targeted and measurable way – from Google Ads and Meta Ads to Facebook Ads, Instagram Ads, Shopify Ads, TikTok Ads, LinkedIn Ads and YouTube Ads.",
      text2:
        "As a lecturer at universities and instructor for the Chamber of Commerce (IHK) and TÜV, I combine academic depth with concrete practical experience. I analyse where your greatest lever lies – and implement the right measures in the right order.",
      approachLabel: "My Approach",
      approach: [
        "Data-driven decisions instead of gut feeling",
        "Cross-channel strategy & execution",
        "Transparent communication & reporting",
        "Budget management, testing roadmap & scaling",
        "Scaling & efficiency",
      ],
    },
    // References
    references: {
      sectionLabel: "References",
      headline: "Trusted by\nleading organisations",
      subtext:
        "From DAX corporations and federal ministries to international embassies and savings banks – a selection of my references as a performance marketing consultant.",
      categories: [
        { id: "corporate", label: "Companies", note: null },
        { id: "public", label: "Public Sector", note: null },
        { id: "education", label: "Lecturing / Teaching", note: null },
      ],
      notePrefix: "Further companies in healthcare:",
      educationUsps: [
        { institution: "IHK Online Marketing Certificate", text: "Instructor for courses preparing participants for the IHK Online Marketing Certificate." },
        { institution: "TÜV · Government Organisations", text: "Trainer for government-organisation training programmes on behalf of TÜV." },
        { institution: "Beuth University Berlin", text: "Visiting lecturer in business administration at Beuth University of Applied Sciences Berlin." },
      ],
    },
    // Stats
    stats: {
      sectionLabel: "Key Figures",
      headline1: "Measurable results",
      headline2: "that count",
      subtext:
        "Numbers that speak for themselves – from over two decades of performance marketing, Google Ads and Meta Ads consulting.",
      items: [
        { value: "20+", label: "Years Experience", desc: "in performance marketing" },
        { value: "80+", label: "Clients & Projects", desc: "national & international" },
        { value: "10+", label: "Ad Platforms", desc: "full expertise" },
        { value: "30+", label: "Embassies & Authorities", desc: "public sector" },
      ],
    },
    // Services — EN translation of the exact DE originals
    services: {
      sectionLabel: "Services",
      headline1: "What I can do",
      headline2: "for you",
      subtext:
        "From Google Ads and Meta Ads to Facebook Ads, Instagram Ads, Shopify Ads, TikTok Ads, LinkedIn Ads and YouTube Ads – I connect the relevant channels into measurable performance marketing.",
      featuredBadge: "Core Service",
      items: [
        {
          title: "Performance Marketing",
          subtitle: "Data-driven. Measurable. Scalable.",
          description:
            "I develop and manage performance campaigns across Google Ads, Meta Ads, Facebook Ads, Instagram Ads, Shopify Ads, TikTok Ads, LinkedIn Ads and YouTube Ads – from strategy and tracking to continuous optimisation. Every measure is aligned to measurable KPIs.",
          tags: ["Google Ads", "Meta Ads", "Facebook Ads", "Instagram Ads", "Shopify Ads", "TikTok Ads", "LinkedIn Ads", "YouTube Ads"],
        },
        {
          title: "Google Ads & Analytics",
          subtitle: "Search, Shopping, Display & more",
          description:
            "Full Google ecosystem expertise: Search, Shopping, Display, Performance Max, Discovery and YouTube Ads – combined with clean conversion tracking and attribution.",
          tags: ["Search", "Shopping", "PMax", "GA4"],
        },
        {
          title: "Meta & Social Ads",
          subtitle: "Facebook Ads, Instagram Ads & more",
          description:
            "Paid Meta Ads on Facebook and Instagram – complemented by Shopify Ads, TikTok Ads, LinkedIn Ads and click-to-WhatsApp campaigns. I connect creatives, audiences, offer and tracking into a coherent social-ads strategy.",
          tags: ["Meta Ads", "Facebook Ads", "Instagram Ads", "Shopify Ads", "TikTok Ads", "LinkedIn Ads"],
        },
        {
          title: "SEO & LLM (AI) Visibility",
          subtitle: "Organic Growth",
          description:
            "Sustainable search engine optimisation for Google and Bing – complemented by a clear information architecture for AI-powered search experiences. I structure relevant content around Google Ads, Meta Ads, Facebook Ads, Instagram Ads and e-commerce so that it is understandable and discoverable for people, search engines and AI systems such as ChatGPT, Claude, Perplexity or Google AI.",
          tags: ["On-Page SEO", "Technical SEO", "LLM / GEO", "ChatGPT", "Claude", "Perplexity"],
        },
        {
          title: "Business Consulting",
          subtitle: "Strategic Consulting",
          description:
            "I find solutions that don't follow a dominant logic. Strategic consulting for companies that want to open up new growth paths.",
          tags: ["Strategy", "Growth", "Innovation"],
        },
        {
          title: "Training & Workshops",
          subtitle: "Knowledge Transfer",
          description:
            "As a lecturer for the IHK, TÜV and as a visiting lecturer at universities, I taught online marketing knowledge in a practical and application-oriented way – for teams and individuals.",
          tags: ["IHK", "TÜV", "University", "Corporate"],
        },
      ],
    },
    // Education — EN translation of the exact DE originals
    education: {
      sectionLabel: "Academic Background",
      headline1: "Solid knowledge,",
      headline2: "global perspective",
      subtext:
        "My academic background combines technical online marketing know-how and performance marketing expertise with an international business perspective – from Berlin to New York to Milan.",
      items: [
        {
          degree: "M.Sc. Online Marketing",
          institution: "Steinbeis School of Management & Innovation",
          location: "Berlin & Munich",
          note: "Specialisation in Performance Marketing & Digital Strategies",
        },
        {
          degree: "NYU Stern School of Business",
          institution: "New York University",
          location: "New York, USA",
          note: "Financial Times: One of the Best Business Schools worldwide",
        },
        {
          degree: "Bocconi School of Management",
          institution: "Università Bocconi",
          location: "Milan, Italy",
          note: "One of the Best International Business Schools",
        },
        {
          degree: "B.A. in Media Management",
          institution: "M.DH Hochschule für Design & Informatik",
          location: "Berlin",
          note: "Foundation for digital marketing and media strategy",
        },
      ],
    },
    // CTA
    cta: {
      badge: "Book a call now",
      headline1: "Where is your",
      headline2: "greatest lever?",
      subtext:
        "In a short, free initial consultation we analyse together which Google Ads, Meta Ads or SEO measures will produce results most quickly and in which order – concrete, measurable and without detours.",
      points: [
        "Initial consultation – free & non-binding",
        "Analysis of your current marketing situation",
        "Concrete recommendations with priorities",
      ],
      ctaButton: "Schedule a Free Consultation",
      contactTitle: "Contact",
      emailLabel: "Email",
      locationLabel: "Location",
      location1: "Dubai, UAE",
      location2: "& Berlin, Germany",
      companyLabel: "Company",
      companyName: "Cem10X Marketing FZCO",
      companyDesc: "Dubai Silicon Oasis, IFZA",
      footerNote:
        "I advise companies nationally and internationally. Feel free to contact me in German or English.",
    },
    // Footer
    footer: {
      tagline: "Performance Marketing · Berlin & Dubai",
      copyright: "All rights reserved.",
      platformsTitle: "Platforms",
      impressumTitle: "Legal Notice",
      company: "Cem10X Marketing FZCO",
      address: "Dubai Silicon Oasis, DDP Building A2, IFZA",
      country: "United Arab Emirates",
      responsible: "Responsible for content: Cem Fernández-Yigit, M.Sc.",
      phoneUAE: "Mobile UAE",
      phoneGER: "Mobile GER",
    },
  },
};

type TranslationType = typeof translations.de;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationType;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    // URL-Parameter ?lang=en oder ?lang=de beim Start auslesen
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get("lang");
    if (langParam === "en" || langParam === "de") return langParam;
    return "de";
  });
  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
